import { useState } from "react";
import { EnvelopeSimple, WhatsappLogo, Clock, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal } from "../Reveal";
import { useLanguage } from "../../context/LanguageContext";
import { WHATSAPP_URL, EMAIL, PHONE_DISPLAY } from "../../i18n/content";
import { API, axios, formatApiErrorDetail } from "../../utils/api";

const inputCls =
  "w-full bg-transparent border border-black/20 rounded-none px-4 py-3.5 text-sm placeholder:text-black/35 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-[box-shadow,border-color] duration-300";

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.toastSuccess);
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contact.toastError);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 border-t border-black/10">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-brand-red mb-6">
            — {t.contact.label}
          </p>
          <h2 data-testid="contact-title" className="font-cabinet font-black tracking-tight text-2xl sm:text-3xl lg:text-5xl leading-tight max-w-3xl mb-5">
            {t.contact.title}
          </h2>
          <p className="text-base md:text-lg text-black/55 max-w-xl mb-16 md:mb-24">{t.contact.desc}</p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-14 lg:gap-10">
          {/* Direct contact */}
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div data-testid="contact-info" className="flex flex-col gap-8">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/45">
                {t.contact.infoTitle}
              </p>

              <a
                data-testid="contact-whatsapp-link"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-5 border border-black/15 bg-white p-6 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-brand-green focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                <span className="p-3.5 bg-brand-green text-white">
                  <WhatsappLogo size={26} weight="duotone" />
                </span>
                <span>
                  <span className="block text-xs font-bold tracking-[0.18em] uppercase text-black/45 mb-1">
                    {t.contact.phoneLabel}
                  </span>
                  <span className="font-cabinet font-bold text-xl text-black group-hover:text-brand-green transition-colors duration-300">
                    {PHONE_DISPLAY}
                  </span>
                </span>
              </a>

              <a
                data-testid="contact-email-link"
                href={`mailto:${EMAIL}`}
                className="group flex items-center gap-5 border border-black/15 bg-white p-6 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-brand-green focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                <span className="p-3.5 bg-brand-ink text-brand-paper">
                  <EnvelopeSimple size={26} weight="duotone" />
                </span>
                <span>
                  <span className="block text-xs font-bold tracking-[0.18em] uppercase text-black/45 mb-1">
                    {t.contact.emailLabel}
                  </span>
                  <span className="font-cabinet font-bold text-lg md:text-xl text-black group-hover:text-brand-green transition-colors duration-300 break-all">
                    {EMAIL}
                  </span>
                </span>
              </a>

              <div className="flex items-center gap-4 text-black/50">
                <Clock size={20} weight="duotone" className="text-brand-green shrink-0" />
                <p className="text-sm leading-relaxed">{t.contact.responseNote}</p>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal className="lg:col-span-7" delay={0.18}>
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="border border-black/15 bg-white p-7 md:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.contact.form.name}
                  className={inputCls}
                  maxLength={120}
                />
                <input
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder={t.contact.form.company}
                  className={inputCls}
                  maxLength={160}
                />
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.form.email}
                  className={inputCls}
                />
                <input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder={t.contact.form.phone}
                  className={inputCls}
                  maxLength={40}
                />
              </div>
              <select
                data-testid="contact-service-select"
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} mb-5 ${form.service ? "text-black" : "text-black/35"}`}
              >
                <option value="">{t.contact.form.servicePlaceholder}</option>
                {t.services.items.map((s) => (
                  <option key={s.id} value={s.title}>
                    {s.title}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder={t.contact.form.message}
                className={`${inputCls} mb-8 resize-none`}
                maxLength={2000}
              />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="group w-full sm:w-auto flex items-center justify-center gap-3 bg-brand-ink text-brand-paper px-9 py-4 font-cabinet font-bold text-base tracking-wide hover:bg-brand-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
              >
                {sending ? t.contact.form.sending : t.contact.form.submit}
                <PaperPlaneTilt size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
